import {
  Close as CloseIcon,
  ExpandMore
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography
} from "@mui/material";

import { CONVERSATION_LENS } from "../../../utils/terminology";
import { CoachingArtifact } from "../hooks/useCoachingArtifacts";

interface PromptAccordionProps {
  title: string;
  content: string | null | undefined;
}

const PromptAccordion = ({ title, content }: PromptAccordionProps) => (
  <Accordion sx={{ boxShadow: "none", "&:before": { display: "none" } }}>
    <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0 }}>
      <Typography variant="body2" fontWeight={500}>
        {title}
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ px: 0 }}>
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {content || "N/A"}
      </Typography>
    </AccordionDetails>
  </Accordion>
);

interface CoachingConfigDialogProps {
  open: boolean;
  onClose: () => void;
  artifact: CoachingArtifact;
}

const CoachingConfigDialog = ({ open, onClose, artifact }: CoachingConfigDialogProps) => {
  const hasAdditionalContext =
    artifact.lens_config?.includeAttributeStats ||
    artifact.lens_config?.includeLensDefinition ||
    artifact.lens_config?.includeTeamBestPractices;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Configuration</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Accordion defaultExpanded sx={{ boxShadow: "none", "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0 }}>
            <Typography variant="body2" fontWeight={500}>
              Model Settings
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Model
              </Typography>
              <Typography variant="body2">
                {artifact.lens_config?.model || "N/A"}
              </Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Temperature
              </Typography>
              <Typography variant="body2">
                {artifact.lens_config?.temperature ?? "N/A"}
              </Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Max Tokens
              </Typography>
              <Typography variant="body2">
                {artifact.lens_config?.maxTokens || "N/A"}
              </Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Selected Attributes
              </Typography>
              <Typography variant="body2">
                {artifact.lens_config?.selectedAttributes?.join(", ") || "N/A"}
              </Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Additional Context
              </Typography>
              {!hasAdditionalContext ? (
                <Typography variant="body2" color="text.secondary">
                  None
                </Typography>
              ) : (
                <List dense disablePadding sx={{ listStyleType: "disc", pl: 2 }}>
                  {[
                    { enabled: artifact.lens_config?.includeAttributeStats, label: "Attribute Performance Statistics" },
                    { enabled: artifact.lens_config?.includeLensDefinition, label: `${CONVERSATION_LENS} Definition` },
                    { enabled: artifact.lens_config?.includeTeamBestPractices, label: "Team Best Practices" }
                  ].filter(item => item.enabled).map(item => (
                    <ListItem key={item.label} disablePadding sx={{ display: "list-item" }}>
                      <ListItemText primary={item.label} slotProps={{ primary: { variant: "body2" } }} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        <PromptAccordion title="System Prompt" content={artifact.system_prompt} />
        <PromptAccordion title="Custom Prompt" content={artifact.lens_config?.customPrompt} />
      </DialogContent>
    </Dialog>
  );
};

export default CoachingConfigDialog;
